/**
 * Smart Safety Timeline (PROJECT_BRIEF §22.2).
 *
 * Vertical day-by-day timeline of a child's safety events, rendered from
 * the analytics firehose. Calm wording, severity-coloured icons.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  BatteryLow,
  Briefcase,
  CheckCircle2,
  Home,
  Lock,
  MapPin,
  School,
  ShieldOff,
  Siren,
  Unlock,
  WifiOff,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usersApi } from '@/api/users';
import { analyticsApi, type TimelineEntry } from '@/api/analytics';
import { ChildPicker } from './ChildPicker';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';
import { useSetFocusedChild } from './KidFocusContext';
import { cn } from '@/lib/utils';

const RANGES = [
  { label: 'Today', days: 1 },
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
] as const;

/** Per-kind icon + chip-colour overrides.
 *
 *  Place-typed events (geofence enter/exit, pickup) use the same
 *  4-colour palette as the map (Home emerald · School blue · Workplace
 *  amber · Other pink) so a row in the timeline visually matches the
 *  circle it came from. Falls back to the severity-based chip otherwise. */
function iconAndChip(entry: TimelineEntry): {
  Icon: typeof Activity;
  chipOverride?: string;
} {
  const { kind, payload } = entry;
  const placeName = ((payload as Record<string, unknown> | null)?.geofence_name as
    | string
    | undefined ?? '').toLowerCase();

  // Place-type detector shared by geofence + pickup events.
  const placeIconFor = (name: string) => {
    if (name.includes('home')) return { Icon: Home, chipOverride: 'bg-emerald-100 text-emerald-700' };
    if (name.includes('school')) return { Icon: School, chipOverride: 'bg-sky-100 text-sky-700' };
    if (name.includes('work') || name.includes('office'))
      return { Icon: Briefcase, chipOverride: 'bg-amber-100 text-amber-800' };
    return { Icon: MapPin, chipOverride: 'bg-pink-100 text-pink-700' };
  };

  if (kind === 'sos.resolved') return { Icon: CheckCircle2 };
  if (kind.startsWith('sos.')) return { Icon: Siren };
  if (kind === 'safety.blocked') return { Icon: ShieldOff };
  if (kind === 'device.battery_low') return { Icon: BatteryLow };
  if (kind === 'device.offline') return { Icon: WifiOff };
  if (kind === 'controls.command') return { Icon: Lock };
  if (kind === 'controls.policy_changed') return { Icon: Unlock };

  // §22.6 Pickup events — direction-typed.
  if (kind === 'pickup.left_school')
    return { Icon: School, chipOverride: 'bg-sky-100 text-sky-700' };
  if (kind === 'pickup.arrived_home' || kind === 'pickup.approaching_home')
    return { Icon: Home, chipOverride: 'bg-emerald-100 text-emerald-700' };

  // Geofence enter/exit — read the place name from payload to pick the
  // right icon + colour. Defaults to a pink MapPin for unknown places.
  if (kind === 'geofence.entered' || kind === 'geofence.exited') {
    return placeIconFor(placeName);
  }

  return { Icon: Activity };
}

function severityStyles(s: TimelineEntry['severity']) {
  return {
    info: { dot: 'bg-zinc-400', chip: 'bg-zinc-100 text-zinc-700' },
    warn: { dot: 'bg-amber-500', chip: 'bg-amber-100 text-amber-800' },
    alert: { dot: 'bg-rose-600', chip: 'bg-rose-100 text-rose-800' },
  }[s];
}

function formatDay(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const y = new Date(today);
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

export function TimelinePage() {
  const { t } = useTranslation();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [days, setDays] = useState<number>(7);
  useSetFocusedChild(selectedChildId);

  const timeline = useQuery({
    queryKey: ['timeline', selectedChildId, days],
    queryFn: () => analyticsApi.timeline(selectedChildId!, days),
    enabled: !!selectedChildId,
    refetchInterval: 30_000,
  });

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.timeline.title')}
        icon={Activity}
        tint="blue"
        description={t('pages.timeline.description')}
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <ChildPicker
          children={children.data ?? []}
          selectedId={selectedChildId}
          onSelect={setSelectedChildId}
        />
        <div className="flex gap-1 rounded-md bg-[color:var(--kz-color-surface-sunken)] p-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={cn(
                'rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                r.days === days
                  ? 'bg-[color:var(--kz-color-surface-card)] text-[color:var(--kz-color-ink-primary)] shadow-sm'
                  : 'text-[color:var(--kz-color-ink-secondary)] hover:text-[color:var(--kz-color-ink-primary)]',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!selectedChildId && (
        <Card className="mt-6">
          <CardContent className="p-8 text-center text-sm text-[color:var(--kz-color-ink-secondary)]">
            {children.data?.length === 0 ? 'Add a child first.' : 'Select a child to view their timeline.'}
          </CardContent>
        </Card>
      )}

      {selectedChildId && timeline.isPending && (
        <Card className="mt-6">
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </CardContent>
        </Card>
      )}

      {selectedChildId && timeline.data && timeline.data.days.length === 0 && (
        <Card className="mt-6">
          <CardContent className="p-0">
            <EmptyState
              icon={Home}
              tint="blue"
              title="A quiet day is a good day"
              description="No events in this period."
            />
          </CardContent>
        </Card>
      )}

      <div className="mt-6 space-y-8">
        {(timeline.data?.days ?? []).map((d) => (
          <section key={d.day}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
              {formatDay(d.day)}
            </h2>
            <ol className="relative space-y-3 border-l border-[color:var(--kz-color-border-subtle)] pl-6">
              {d.entries.map((e, i) => {
                const { Icon, chipOverride } = iconAndChip(e);
                const styles = severityStyles(e.severity);
                return (
                  <li key={i} className="relative">
                    <span
                      className={cn(
                        'absolute -left-[1.65rem] top-1 grid size-3 place-items-center rounded-full ring-4 ring-[color:var(--kz-color-surface-sunken)]',
                        styles.dot,
                      )}
                    />
                    <div className="flex items-start gap-3 rounded-lg border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-3">
                      <span
                        className={cn(
                          'grid size-8 shrink-0 place-items-center rounded-md',
                          chipOverride ?? styles.chip,
                        )}
                      >
                        <Icon className="size-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="font-medium">{e.label}</span>
                          <span className="shrink-0 tabular-nums text-xs text-[color:var(--kz-color-ink-muted)]">
                            {new Date(e.occurred_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </p>
                        {e.detail && (
                          <p className="mt-0.5 truncate text-xs text-[color:var(--kz-color-ink-secondary)]">
                            {e.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}
